import { useEffect, useRef } from 'react';

/**
 * Poll `fn` every `intervalMs` while active; stops when fn resolves true. Errors are swallowed
 * (next tick retries).
 *
 * `pulse`, if given, forces an immediate poll whenever its value changes while active — e.g. on
 * `visibilitychange` (E3), so the user doesn't wait out the rest of a 3s tick after coming back
 * from their banking app. A pulse still respects the in-flight guard: it's a no-op while a poll
 * from either source (the interval or a pulse) is already running. Callers that don't need this
 * simply omit the argument — its default is `undefined`, which never changes, so the pulse
 * effect never fires for them.
 *
 * `pollOnMount`, if true, also fires one poll immediately when polling (re)starts — i.e. on
 * mount, and again whenever `active` flips back to true (e.g. E15's "Check again") — instead of
 * waiting out the first `intervalMs` tick. Useful when the awaited state may already be terminal
 * by the time the view appears (E9: an already-ACTIVE ref on `?duitnow_ref=` resume shouldn't
 * show a 3s "waiting" flash first). Defaults to false, so existing callers are unaffected.
 */
export function usePolling(
  fn: () => Promise<boolean>,
  intervalMs: number,
  active: boolean,
  pulse?: unknown,
  pollOnMount?: boolean
): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;

  const stoppedRef = useRef(false);
  const inFlightRef = useRef(false);
  const tickRef = useRef<() => void>(() => {});

  useEffect(() => {
    if (!active) return;
    stoppedRef.current = false;

    const tick = async () => {
      if (stoppedRef.current || inFlightRef.current) return;
      inFlightRef.current = true;
      try {
        if (await fnRef.current()) {
          stoppedRef.current = true;
          clearInterval(id);
        }
      } catch {
        /* retry next tick */
      } finally {
        inFlightRef.current = false;
      }
    };
    tickRef.current = tick;

    if (pollOnMount) void tick();
    const id = setInterval(tick, intervalMs);
    return () => {
      stoppedRef.current = true;
      clearInterval(id);
    };
  }, [intervalMs, active, pollOnMount]);

  const pulseSeenRef = useRef(pulse);
  useEffect(() => {
    if (pulseSeenRef.current === pulse) return;
    pulseSeenRef.current = pulse;
    if (active) tickRef.current();
  }, [pulse, active]);
}
